import { forwardRef, ComponentPropsWithoutRef, ReactNode } from "react";
import clsx from "clsx";
import ErrorBoundary from "../error-boundary";

import styles from "./table.module.scss";
import { SizeMap, SizePropsWithXs, DefaultSizes, Xs_Sizes } from "../sizes";

export type TableProps = {
  /** Set to true to enable intraction states (hover, etc.) */
  interactive?: boolean;
  /** Optional extra class(es) */
  className?: string;
  /** `<TableHead/>`, `<TableBody/>` and `<TableCaption/>` */
  children: ReactNode;
} & ComponentPropsWithoutRef<"table"> &
  SizePropsWithXs;

const _Table = forwardRef<HTMLTableElement, TableProps>(
  ({ interactive, className, children, size = "m", ...rest }, ref) => {
    const classes = [
      styles.table,
      styles[SizeMap[size]],
      { [styles.tableInteractive]: interactive },
      className,
    ];

    return (
      <ErrorBoundary>
        <table ref={ref} {...rest} className={clsx(classes)}>
          {children}
        </table>
      </ErrorBoundary>
    );
  }
);

const Table = Object.assign(_Table, {
  sizes: Object.keys({ ...DefaultSizes, ...Xs_Sizes }).reduce(
    (obj, key) => ({ ...obj, [key]: key }),
    {}
  ) as RecordKeys<SizePropsWithXs>,
});

Table.displayName = "Table";

export default Table;
